import { EJobPriority, EJobRole, EJobStatus, EJobType, EProductType, type IJob } from '@quesmed/types/models';
import { FaRegEye } from 'react-icons/fa6';
import { FiExternalLink } from 'react-icons/fi';
import { GrFormEdit } from 'react-icons/gr';
import { Link } from 'react-router-dom';
import { jobPriorityColorMap, jobStatusColorMap, ROUTES } from '@/config';
import { Chip, cn, Progress } from '@/core/ui';
import { formatDateFromUnix } from '@/utils/date';
import { getPercentage } from '@/utils/string';
import { JobItemActions } from './JobItemActions';
import { JobSummary } from './JobSummary';

export function JobItem({ item, className, index }: { item: IJob; className?: string; index: number }) {
  const _writer = item.members?.find((m) => m.role === EJobRole.WRITER)?.user;
  const _editor = item.members?.filter((m) => m.role === EJobRole.REVIEWER)?.map((m) => m.user);
  const writer = _writer ? `${_writer.firstName} ${_writer.lastName}` : '-';
  const editor =
    _editor.length > 0 ? _editor.map((e) => `${e.firstName} ${e.lastName.charAt(0)}.`).join(', ') : '-';
  return (
    <div
      key={item.id}
      className={cn(
        'flex flex-col group relative',
        'shrink-0',
        'bg-content1',
        'rounded-lg',
        'border border-default-200',
        'hover:border-default-300',
        // 'hover:-translate-y-[1px] will-change-transform hover:shadow-small',
        'transition-all duration-300',
        'shadow-sm backdrop-blur-xl',
        className,
      )}
    >
      {/* <div
        className={cn(
          'absolute inset-0 opacity-0 group-hover:opacity-100',
          'transition-opacity duration-300',
        )}
      >
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:4px_4px]' />
      </div> */}

      <div className='p-3.5'>
        <div
          className={cn(
            'flex items-start justify-between',
            // 'border-b border-default-200 px-2 -mx-2 pb-2',
          )}
        >
          <div className=''>
            <div className='flex flex-col items-start'>
              <div className='flex items-center gap-1'>
                <Chip
                  title={`Job ${index}`}
                  color='default'
                  classNames={{
                    content: 'text-[10px]',
                    base: 'h-4.5 px-0.5 border border-default-200 rounded-md',
                  }}
                  className={'capitalize'}
                  size='sm'
                  variant='solid'
                >
                  {index}
                </Chip>
                <Link
                  to={ROUTES.CONTENT.JOB.DETAIL.replace(':id', item.id)}
                  className='flex items-center gap-1 hover:underline decoration-rose-500'
                >
                  <span className='text-sm max-w-44 font-medium text-default-600 truncate'>{item.name}</span>
                  <FiExternalLink className='size-3.5 text-default-400' />
                </Link>
              </div>
              <span className='text-[10px] leading-4 font-mono text-foreground-400'>{item.id}</span>
            </div>
          </div>

          <JobItemActions
            jobId={item.id}
            name={item.name}
            status={item.status}
            trial={item.trial}
            createdBy={item.createdByUser?.id}
          />
        </div>
        <div className='mt-1.5'>
          <Progress
            title='Record Progress'
            classNames={{
              base: 'max-w-md',
              indicator: 'bg-gradient-to-r from-pink-500 to-yellow-500',
              label: 'tracking-wider font-medium text-default-600',
              value: 'text-foreground/60 text-xs -mb-1',
            }}
            radius='sm'
            size='sm'
            value={getPercentage(item.recordCount, item.totalRecords)}
          />

          {/* <Progress
            title='Published Progress'
            classNames={{
              base: 'max-w-md mt-1',
              indicator: 'bg-gradient-to-r from-cyan-400 to-blue-500',
              label: 'tracking-wider font-medium text-default-600',
              value: 'text-foreground/60 text-xs -mb-1',
            }}
            radius='sm'
            size='sm'
            value={getPercentage(item.publishedCount, item.totalRecords)}
          /> */}
          {/* <div className='flex items-center justify-between text-xs'>
            <div className='flex items-center text-[10px] gap-2'>
              {Math.round((item.progress / 100) * item.totalRecords)} / {item.totalRecords}
            </div>
            <span className='text-default-600 text-[10px]'>{item.progress}%</span>
          </div> */}
        </div>

        {/* <p title={item.description} className='text-xs text-default-500 line-clamp-1'>
          {item.description}
        </p> */}

        <div className='flex items-center gap-1 my-3'>
          <JobSummary
            chatCount={item.chatCount}
            resolvedCount={item.resolvedCount}
            remarkCount={item.remarkCount}
            publishedCount={item.publishedCount}
            memberCount={item.memberCount}
            recordCount={item.recordCount}
            totalRecords={item.totalRecords}
            assetCount={item.assetCount}
          />

          <span className='text-xs w-full text-end justify-self-end text-default-400'>
            Due {formatDateFromUnix(item.dueDate as number, 'DD/MM/YY')}
          </span>
        </div>

        <div className='flex items-center gap-1 mb-3'>
          <Chip
            radius='sm'
            title={`Status of Job: ${EJobStatus[item.status]}`}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
            className={cn('capitalize', jobStatusColorMap[item.status]?.className)}
            // startContent={jobStatusColorMap[item.status].icon}
            size='sm'
            variant='flat'
          >
            {EJobStatus[item.status]}
          </Chip>
          <Chip
            radius='sm'
            title={`Product of Job: ${EProductType[item.productId]}`}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
            className={cn('capitalize bg-default-100 text-default-500 border border-default-200')}
            // startContent={jobStatusColorMap[item.status].icon}
            size='sm'
            variant='flat'
          >
            {EProductType[item.productId]}
          </Chip>
          <Chip
            radius='sm'
            title={`Type of Job: ${EJobType[item.type]}`}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
            className={cn('capitalize border border-default-300')}
            // startContent={jobStatusColorMap[item.status].icon}
            size='sm'
            variant='flat'
          >
            {EJobType[item.type].charAt(0)}
          </Chip>

          <Chip
            radius='sm'
            title={`Priority of Job: ${EJobPriority[item.priority]}`}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1' }}
            className={cn('capitalize', jobPriorityColorMap[item.priority].className)}
            size='sm'
            variant='flat'
          >
            {EJobPriority[item.priority as EJobPriority].charAt(0)}
          </Chip>

          <span className='text-xs w-full text-end justify-self-end text-default-400'>
            Updated {formatDateFromUnix(item.updatedAt as number, 'DD/MM/YY')}
          </span>
        </div>

        <div className='flex items-center gap-1 justify-between'>
          <Chip
            startContent={<GrFormEdit className='size-3.5 text-warning-500' />}
            title={'Writer'}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-warning-200' }}
            className={cn('capitalize')}
            size='sm'
            radius='sm'
            color='warning'
            variant='flat'
          >
            {writer}
          </Chip>

          <Chip
            startContent={<FaRegEye className='size-3 text-secondary-500 mr-0.5' />}
            title={'Editor'}
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-secondary-100' }}
            className={cn('capitalize')}
            size='sm'
            radius='sm'
            color='secondary'
            variant='flat'
          >
            {editor}
          </Chip>
        </div>
      </div>
    </div>
  );
}
